import { itemIconUrl } from './icons';
import { Hotbar, HOTBAR_ITEMS, HOTBAR_SLOTS } from '../game/hotbar';
import { PlayerInventory, ITEMS, type ItemId } from '../game/inventory';
import { DragController } from './drag';
import { DurabilityStore } from '../game/durability';
import { DURABILITY } from '../config';
import { SK } from './skin';

// On-screen hotbar: a bottom-center row of the six slots (1..6). Each shows its key digit, the item's
// name and stack count, and a durability bar for tools that wear. Slots hold REAL stacks now (the bar
// is PlayerInventory storage).
//
// Interaction modes:
//  - a screen is open (inventory/storage/crafting): free drag source + drop target, exactly as before.
//  - THIRD PERSON, no screen: the bar is LIVE under the mouse. Click a slot to switch to it; HOLD the
//    left button ~0.5s to lift the stack (it highlights), then drag: onto another slot to move/swap, or
//    release far from the bar to FLING it on the ground. Releasing anywhere near the bar (the
//    forgiveness ring) snaps it back -- no accidental disposal.

const HOLD_TO_DRAG_MS = 500;
const HOLD_CANCEL_PX = 14;       // moving further than this before the hold arms = not a click, not a drag
export const BAR_FORGIVE_PX = 130; // release within this ring around the bar = snap back (no fling)

export class HotbarUi {
  private readonly root: HTMLDivElement;
  private readonly cells: HTMLDivElement[] = [];
  private screenMode = false; // an inventory/storage/crafting screen is up
  private liveMode = false;   // third person, no screen: click-select + hold-to-drag
  private hold: { slot: number; x: number; y: number; timer: number } | null = null;

  constructor(
    private readonly hotbar: Hotbar,
    private readonly inv: PlayerInventory,
    private readonly drag: DragController,
    private readonly durability: DurabilityStore,
  ) {
    this.root = document.createElement('div');
    this.root.dataset.ui = '1'; // clicks on the bar must never fire the weapon (input checks this)
    this.root.style.cssText = rootCss();

    for (let slot = 1; slot <= HOTBAR_SLOTS; slot++) {
      const cell = document.createElement('div');
      cell.dataset.drop = 'hotbar';
      cell.dataset.slot = String(slot);
      cell.addEventListener('mousedown', (e) => this.onCellDown(slot, e));
      cell.addEventListener('mouseup', (e) => this.onCellUp(slot, e));
      this.root.appendChild(cell);
      this.cells.push(cell);
    }
    window.addEventListener('mousemove', (e) => this.onMove(e));
    window.addEventListener('mouseup', () => this.clearHold());

    document.body.appendChild(this.root);
    this.drag.registerLift(this.root);

    this.hotbar.onChange = () => this.refresh();
    this.inv.subscribe(() => this.refresh()); // stack counts change without a slot change
    this.durability.subscribe(() => this.refresh()); // redraw the wear bar as a tool degrades
    this.refresh();
  }

  // Screen mode: the bar is a live drop/drag target while an inventory or storage screen is open.
  // Raise it ABOVE the screen backdrops (z 13) then, so it renders undimmed and mousedowns reach the
  // cells (drag out of the bar into the grid and back); the drag ghost (z 60) still floats over it.
  setInteractive(on: boolean): void {
    this.screenMode = on;
    this.root.style.zIndex = on ? '30' : '9';
    this.applyPointerEvents();
    this.refresh();
  }

  // Live mode: third person with a free cursor (no screen). Click selects, hold-drags.
  setLiveMode(on: boolean): void {
    if (on === this.liveMode) return;
    this.liveMode = on;
    if (!on) this.clearHold();
    this.applyPointerEvents();
  }

  // How far a point is from the bar's rect (0 = inside). The fling threshold reads this.
  distanceFromBar(x: number, y: number): number {
    const r = this.root.getBoundingClientRect();
    const dx = Math.max(r.left - x, 0, x - r.right);
    const dy = Math.max(r.top - y, 0, y - r.bottom);
    return Math.hypot(dx, dy);
  }

  private applyPointerEvents(): void {
    this.root.style.pointerEvents = this.screenMode || this.liveMode ? 'auto' : 'none';
  }

  private get interactive(): boolean {
    return this.screenMode || this.liveMode;
  }

  private onCellDown(slot: number, e: MouseEvent): void {
    if (e.button !== 0 || !this.interactive) return;
    const stack = this.hotbar.stackAt(slot);
    if (this.screenMode) {
      if (!stack) return;
      this.drag.beginCandidate({ source: 'hotbar', id: stack.id, slot, n: stack.n }, HOTBAR_ITEMS[stack.id as keyof typeof HOTBAR_ITEMS]?.name ?? stack.id, e);
      return;
    }
    // LIVE mode: arm the hold; a quick release = click-to-select (handled in onCellUp).
    e.preventDefault();
    this.clearHold();
    if (!stack) return; // empty slot: selection still happens on mouseup, nothing to drag
    const timer = window.setTimeout(() => {
      if (!this.hold) return;
      const s = this.hotbar.stackAt(this.hold.slot);
      this.hold = null;
      if (!s) return;
      this.cells[slot - 1].style.outline = '2px solid rgba(255,220,120,0.95)'; // lift highlight
      window.setTimeout(() => { this.cells[slot - 1].style.outline = ''; }, 250);
      this.drag.beginForced({ source: 'hotbar', id: s.id, slot, n: s.n }, `${HOTBAR_ITEMS[s.id as keyof typeof HOTBAR_ITEMS]?.name ?? s.id} (${s.n})`, e.clientX, e.clientY);
    }, HOLD_TO_DRAG_MS);
    this.hold = { slot, x: e.clientX, y: e.clientY, timer };
  }

  private onCellUp(slot: number, e: MouseEvent): void {
    if (e.button !== 0 || !this.liveMode || this.screenMode) return;
    if (this.drag.dragging) return; // the hold armed and a drag is in flight; the drop handler owns this release
    if (this.hold && this.hold.slot === slot) {
      this.clearHold();
      this.hotbar.select(slot); // quick press = switch to this slot
    } else if (!this.hold) {
      this.hotbar.select(slot); // empty-slot click (no hold was armed): still selects
    }
  }

  private onMove(e: MouseEvent): void {
    if (!this.hold) return;
    if (Math.abs(e.clientX - this.hold.x) + Math.abs(e.clientY - this.hold.y) > HOLD_CANCEL_PX) this.clearHold();
  }

  private clearHold(): void {
    if (!this.hold) return;
    window.clearTimeout(this.hold.timer);
    this.hold = null;
  }

  refresh(): void {
    const active = this.hotbar.activeSlot();
    for (let i = 0; i < this.cells.length; i++) {
      const slot = i + 1;
      const cell = this.cells[i];
      const stack = this.hotbar.stackAt(slot);
      const id = stack?.id ?? null;
      cell.style.cssText = cellCss(slot === active, this.interactive && !!id);
      cell.dataset.drop = 'hotbar';
      cell.dataset.slot = String(slot);
      cell.title = id ? (HOTBAR_ITEMS[id as keyof typeof HOTBAR_ITEMS]?.name ?? id) : '';
      cell.replaceChildren();

      // Slot index, top-left corner (faint mono, per the slot spec).
      const key = document.createElement('div');
      key.textContent = slotKeyLabel(slot);
      key.style.cssText = css('position:absolute', 'top:4px', 'left:6px', `font:700 10px ${SK.font}`, `color:${SK.faint}`, 'pointer-events:none');
      cell.appendChild(key);

      const hasDura = !!id && DURABILITY[id] != null; // a tool that wears: its bar seats flush at the slot bottom
      if (id) {
        // Item icon: the owner's 80x80 art when it exists, else the classic colour swatch.
        const icon = document.createElement('div');
        const url = itemIconUrl(id);
        const hex = '#' + (ITEMS[id as ItemId]?.color ?? 0x8a8f96).toString(16).padStart(6, '0');
        icon.style.cssText = url
          ? css('position:absolute', 'top:44%', 'left:50%', 'transform:translate(-50%,-50%)', 'width:54px', 'height:54px', `background:url('${url}') center / contain no-repeat`, 'pointer-events:none', 'filter:drop-shadow(0 2px 3px rgba(0,0,0,0.55))')
          : css('position:absolute', 'top:44%', 'left:50%', 'transform:translate(-50%,-50%)', 'width:38px', 'height:38px', 'border-radius:9px', `background:${hex}`, 'box-shadow:inset 0 2px 4px rgba(255,255,255,0.18),inset 0 -3px 6px rgba(0,0,0,0.35)', 'pointer-events:none');
        cell.appendChild(icon);

        // Item name (reference HUD look): uppercase, ellipsised, dark gradient behind. When the item has a
        // durability bar, the name sits just ABOVE that flush bar (and drops its bottom rounding); otherwise
        // it seats at the very bottom of the slot.
        const name = document.createElement('div');
        name.textContent = HOTBAR_ITEMS[id as keyof typeof HOTBAR_ITEMS]?.name ?? id;
        name.style.cssText = css('position:absolute', 'left:0', 'right:0', hasDura ? 'bottom:6px' : 'bottom:4px',
          `font:700 9px/1.1 ${SK.font}`, 'letter-spacing:0.08em', 'text-align:center',
          `color:${SK.dim}`, 'text-transform:uppercase', 'white-space:nowrap', 'overflow:hidden', 'text-overflow:ellipsis',
          'text-shadow:0 1px 3px rgba(0,0,0,0.9)', 'pointer-events:none');
        cell.appendChild(name);
      }

      // Stack count, top-right (the bottom edge belongs to the name label + durability bar).
      if (stack && stack.n > 1) {
        const badge = document.createElement('div');
        badge.textContent = String(stack.n);
        badge.style.cssText = css('position:absolute', 'right:6px', 'top:4px', `font:700 ${SK.fsMicro} ${SK.font}`, 'font-variant-numeric:tabular-nums', `color:${SK.textHi}`, 'text-shadow:0 1px 2px #000', 'pointer-events:none');
        cell.appendChild(badge);
      }

      // DURABILITY: the spec's 3px bottom bar, seated just inside the slot edge. Semantic wear
      // colors: good -> warn -> bad as the tool grinds down.
      if (id && DURABILITY[id] != null) {
        const frac = this.durability.frac(id);
        const track = document.createElement('div');
        track.style.cssText = css('position:absolute', 'left:2px', 'right:2px', 'bottom:0', 'height:3px', 'border-radius:2px', 'background:rgba(0,0,0,0.55)', 'overflow:hidden', 'pointer-events:none');
        const fill = document.createElement('div');
        const col = frac > 0.5 ? SK.good : frac > 0.25 ? SK.warn : SK.bad;
        fill.style.cssText = css('position:absolute', 'left:0', 'top:0', 'bottom:0', `width:${Math.round(frac * 100)}%`, `background:${col}`, 'border-radius:2px');
        track.appendChild(fill);
        cell.appendChild(track);
        // BROKEN tool: a red X over the item (it's not deleted, just unusable until you craft a new one).
        if (this.durability.broken(id)) {
          const x = document.createElement('div');
          x.textContent = '✕';
          x.style.cssText = css('position:absolute', 'inset:0', 'display:flex', 'align-items:center', 'justify-content:center', 'font-size:28px', 'font-weight:800', `color:${SK.bad}`, 'pointer-events:none');
          cell.appendChild(x);
        }
      }
    }
  }
}

// --- helpers ---

function css(...lines: string[]): string {
  return lines.join(';');
}

function rootCss(): string {
  // Style guide "hotbar-glass-lt": a glass strip carrying 72px slots at an 8px gap.
  return css(
    'position:fixed', 'left:50%', 'bottom:16px', 'transform:translateX(-50%)', 'z-index:9',
    'display:flex', `gap:${SK.rSm ? '8px' : '8px'}`, 'padding:8px', `border-radius:${SK.rLg}`,
    `background:${SK.glassLt}`, `backdrop-filter:blur(${SK.blur})`, `-webkit-backdrop-filter:blur(${SK.blur})`,
    `font-family:${SK.font}`, 'pointer-events:none',
  );
}

function cellCss(active: boolean, grabbable: boolean): string {
  return css(
    'position:relative', 'width:72px', 'height:72px', 'flex:none', `border-radius:${SK.rMd}`,
    'background:rgba(10,8,6,0.72)',
    // The SELECTED slot lifts with the ACCENT ring (selection = accent, style guide law 1). The
    // transition eases the lift when you switch slots -- refresh() reuses each cell element (only
    // cssText is rewritten), so the transform change 0<->-6px animates.
    `transition:transform 160ms ${SK.ease},box-shadow 160ms ${SK.ease},border-color 160ms ${SK.ease}`,
    active ? 'transform:translateY(-6px)' : 'transform:translateY(0)',
    active ? `border:1px solid ${SK.accent}` : `border:1px solid ${SK.border}`,
    active
      ? `box-shadow:0 0 0 1px ${SK.accent},0 0 14px ${SK.accentGlow}`
      : 'box-shadow:none',
    grabbable ? 'cursor:grab' : 'cursor:default',
  );
}

// Slot 10 is the '0' key; slots 1..9 print their own digit.
function slotKeyLabel(slot: number): string {
  return slot === 10 ? '0' : String(slot);
}
